/**
 * Size Guide Tabs
 *
 * Vanilla JS module that converts child-block trigger buttons into a
 * tab bar inside the size-guide modal.  Follows the same delegation
 * pattern used by size-chart.js — no web component needed.
 *
 * Progressive enhancement: without JS the tab bar is empty (:empty →
 * display:none) and every panel remains visible.  A single tab trigger
 * is hidden and panels stay visible — no tab bar is shown.
 */

const TAB_BAR_SEL = ".sizeGuide__tabBar";
const BODY_INNER_SEL = ".sizeGuide__bodyInner";
const TRIGGER_SEL = '[class*="__tabTrigger"]';

const TAB_CLS = "sizeGuide__tab";
const TAB_ACTIVE_CLS = "sizeGuide__tab--active";
const PANEL_ACTIVE_CLS = "sizeGuide__tabPanel--active";
const INIT_ATTR = "data-tabs-initialized";

const initTabBar = tabBar => {
  const bodyInner = tabBar.parentElement.querySelector(BODY_INNER_SEL);
  if (!bodyInner || bodyInner.hasAttribute(INIT_ATTR)) return;

  const panels = Array.from(bodyInner.children);
  if (panels.length === 0) return;

  const tabs = [];

  for (const panel of panels) {
    const trigger = panel.querySelector(TRIGGER_SEL);
    if (!trigger) continue;

    tabs.push({ tab: trigger, panel });
  }

  // Nothing to tab between — bail out, all panels stay visible
  if (tabs.length === 0) return;

  // Single panel — hide trigger, skip tab bar
  if (tabs.length === 1) {
    tabs[0].tab.hidden = true;
    return;
  }

  for (const { tab } of tabs) {
    tab.className = TAB_CLS;
    tab.setAttribute("role", "tab");
    tab.setAttribute("aria-selected", "false");
    tabBar.appendChild(tab);
  }

  // Activate first tab + panel
  tabs[0].tab.classList.add(TAB_ACTIVE_CLS);
  tabs[0].tab.setAttribute("aria-selected", "true");
  tabs[0].panel.classList.add(PANEL_ACTIVE_CLS);

  // Mark initialised
  bodyInner.setAttribute(INIT_ATTR, "");
  tabBar.setAttribute("role", "tablist");

  // Click delegation on tab bar
  tabBar.addEventListener("click", event => {
    const clickedTab = event.target.closest("." + TAB_CLS);
    if (!clickedTab || !tabBar.contains(clickedTab)) return;

    for (const { tab, panel } of tabs) {
      const isTarget = tab === clickedTab;
      tab.classList.toggle(TAB_ACTIVE_CLS, isTarget);
      tab.setAttribute("aria-selected", String(isTarget));
      panel.classList.toggle(PANEL_ACTIVE_CLS, isTarget);
    }
  });
};

const initAllTabBars = () => {
  const tabBars = document.querySelectorAll(TAB_BAR_SEL);
  for (const tabBar of tabBars) {
    initTabBar(tabBar);
  }
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initAllTabBars);
} else {
  initAllTabBars();
}

document.addEventListener("section:updated", () => queueMicrotask(initAllTabBars));
document.addEventListener("section:morph", () => queueMicrotask(initAllTabBars));
